import styled, { css } from 'styled-components';
import theme from 'src/theme';

export interface IButtonProps {
    isSelected?: boolean;
    disabled?: boolean;
    altColor?: boolean;
    onClick?: (eventTypeId: number) => void;
}

export interface IPositionProps {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
}
export interface IPanelProps {
    alignLeft?: boolean;
    alignCenter?: boolean;
}

export const IconButton = styled.button<IButtonProps>`
    color: ${(props) => (props.altColor ? theme.colors.text.white : theme.colors.text.tabLineButton)};
    background: ${({ theme }) => theme.colors.background.transparent};
    border: 1px solid ${({ theme }) => theme.colors.border.transparent};
    font-size: 1rem;
    cursor: pointer;
    padding: 3px 6px;
    :hover {
        background: ${({ theme }) => theme.colors.button.icon.hover};
        border: 1px solid ${({ theme }) => theme.colors.border.icon};
    }
    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}
`;


export const TextButton = styled.button<IButtonProps>`
    font-weight: 600;
    font-size: 0.75rem;
    padding: 10px 10px;
    border: 1px solid #ddd;
    height: 40px;
    border-radius: 3px;
    transition: background-color 50ms cubic-bezier(0.4, 0, 0.2, 1);
    ${(props) => props.theme.selectableElement(props.isSelected)}
    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: unset;
            opacity: 0.6;
        `}

    box-shadow: ${({ theme }) => theme.colors.button.default.shadow} 0px 1px 10px -2px;
    &.full-width {
        flex: 0 0 50%;
        text-align: center;
    }
    &.inline {
        height: auto;
        padding: 4px 8px;
    }
`;


export const MainButton = styled.button<IButtonProps>`
    border: 1px solid ${({ theme }) => theme.colors.button.default.border};
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 50ms cubic-bezier(0.4, 0, 0.2, 1);
    ${(props) => props.theme.selectableElement(props.isSelected)}
`;

export const EmptyButton = styled.button`
    font-size: 0.75rem;
    border: 0;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
        `}
`;

export const PrimaryButton = styled(MainButton) <IButtonProps>`
    font-weight: 600;
    font-size: 0.75rem;
    height: 40px;
    background: ${({ theme }) => theme.colors.button.default.background};
    color: ${({ theme }) => theme.colors.button.default.color};
    box-shadow: ${({ theme }) => theme.colors.button.default.shadow} 0px 1px 10px -2px;
    padding: 10px 20px;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
        `}
`;

export const MediumButton = styled(MainButton) <IButtonProps>`
    font-weight: 600;
    font-size: 0.75rem;
    height: 30px;
    background: ${({ theme }) => theme.colors.button.default.background};
    color: ${({ theme }) => theme.colors.button.default.color};
    box-shadow: ${({ theme }) => theme.colors.button.default.shadow} 0px 1px 10px -2px;
    padding: 5px 10px;
    margin-right: 2px;
    min-width: 2.1rem;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
        `}
`;


export const TabLineButton = styled(PrimaryButton) <IButtonProps>`
    height: 30px;
    padding: 0 10px;
    font-size: 0.75rem;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
        `}
    box-shadow: ${({ theme }) => theme.colors.shadow.panel} 0px 1px 10px -2px;
`;

export const TabLineDDButton = styled(PrimaryButton) <IButtonProps>`
    height: 30px;
    padding: 0 10px;
    font-size: 0.75rem;
    position: relative;
    z-index: 2;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
        `}
    box-shadow: ${({ theme }) => theme.colors.button.default.shadow} 0px 1px 10px -2px;
    .dd-main {
        display: flex;
        justify-content: space-betweem;
        align-items: center;
        grid-gap: 5px;
    }
    .dd-list {
        position: absolute;
        background: #fff;
        right: 0;
        border: 1px solid #ddd;
        top: 30px;
        width: 125px;
        z-index: 1;
        li {
            padding: 0;
            .first-item {
                border-bottom: 1px solid #ddd;
            }
            button {
                border-radius: 0;
                padding: 5px;
                width: 100%;
                :hover {
                    background: #eee;
                    border: 0;
                }
            }
        }
    }
`;

export const ListButton = styled(PrimaryButton) <IButtonProps>`
    height: 24px;
    padding: 2px 15px;
    font-size: 0.75rem;
    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
            hover {
                background: none;
            }
        `}
    box-shadow: ${({ theme }) => theme.colors.button.default.shadow} 0px 1px 10px -2px;
`;

export const ExitButton = styled.button<IButtonProps>`
    font-size: 1.1rem;
    background: none;
    border: 0;
    cursor: pointer;
    margin: 0 0 0 5px;
    padding: 0 10px;
    &:hover {
        background: ${({ theme }) => theme.colors.button.exitButton.hover};
        border: 0;
    }

    ${({ isSelected }) => isSelected && css``}
`;

export const EllipsisVerticalButton = styled.button<IButtonProps>`
    font-size: 0.9rem;
    color: #04265a;
    background: #fff;
    border: 1px solid #ccc;
    cursor: pointer;
    padding: 3px 10px;
    margin: 0 6px 0 0;

    ${({ isSelected }) =>
        isSelected &&
        css`
            color: #04265a;
            background: #fff;
            border: 1px solid #ccc;
        `}
`;

export const InfoButton = styled.button<IButtonProps>`
    cursor: pointer;
    padding: 4px 8px;
    margin: 4px 0px 0 0 ${({ isSelected }) => isSelected && css``};
`;

export const SortButton = styled.button<IButtonProps>`
    font-size: 0.9rem;
    color: #04265a;
    background: #fff;
    border: 1px solid #ccc;
    cursor: pointer;
    padding: 3px 10px;
    margin: 0 6px 0 0;
    box-shadow: 0 1px 10px -2px #ccc;
    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}
`;

export const PrinterButton = styled.button<IButtonProps>`
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.button.printerButton.color};
    background: ${({ theme }) => theme.colors.button.printerButton.background};
    border: 1px solid #ccc;
    cursor: pointer;
    padding: 3px 10px;
    margin: 0 6px 0 0;

    ${({ isSelected }) =>
        isSelected &&
        css`
            color: #04265a;
            background: #fff;
            border: 1px solid #ccc;
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: unset;
            &:hover {
                background: #fff;
            }
        `}
`;

export const TrashButton = styled.button<IButtonProps>`
    cursor: pointer;
    font-size: 0.9rem;
    color: #04265a;
    background: #fff;
    border: 1px solid #ccc;
    cursor: pointer;
    padding: 3px 10px;
    margin: 0 6px 0 0;

    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: unset;
            background-color: #cccccc;
            color: #666666;
            opacity: 0.7;
        `}
`;

export const ExpandPanelButton = styled.button<IPositionProps>`
    position: absolute;
    margin-left: -17px;

    ${({ top }) =>
        top &&
        css`
            top: ${top};
        `}
    ${({ right }) =>
        right &&
        css`
            right: ${right};
        `}
    ${({ left }) =>
        left &&
        css`
            left: ${left};
        `}

    color: #04265a;
    background: #ddd;
    border: 1px solid #ccc;
    cursor: pointer;
    border-radius: 50%;
    font-size: 12px;
    z-index: 1;
`;

type TabsProps = {
    tabs?: string;
};

export const TabsWrapper = styled.div<TabsProps>`
    display: flex;
    align-items: end;
    flex: 0 0 35%;
    margin-right: 5px;
`;

export const TabButton = styled.button<IButtonProps>`
    display: flex;
    flex-direction: column;
    position: relative;
    font-weight: 600;
    font-size: 0.7rem;
    background: none;
    text-align: center;
    color: #04265a;
    cursor: pointer;
    background: #fff;
    font-weight: 500;
    border-right: 0;
    border-radius: 3px 3px 0 0;
    margin: 0 1px 0 0;
    border: 1px solid #ddd;
    padding: 10px;
    height: 50px;
    color: #1f2c03;
    min-width: 6rem;
    font-size: 0.8rem;
    justify-content: center;
    margin: 0;

    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            color: ${theme.legacyColors.selectedColor};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            opacity: 0.5;
        `}


        &.seperate-from-group {
        margin-left: 10px;
    }
`;

export const TabText = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    font-weight: 500;
    height: 100%;
`;

export const IconsTabWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    height: 15px;
    width: 100%;
`;

export const SmallTabButton = styled(TabButton) <IButtonProps>`
    padding: 5px 10px;
    height: 42px;
    width: 60px;
    position: relative;
    text-align: left;
    &.add-order {
        font-size: 1rem;
        width: 40px;
        border-radius: 0;
        align-items: center;
        padding: 7px 0;
        &:disabled {
            opacity: 0.6;
            &:hover {
                background: none;
                cursor: default;
            }
        }
        .show-text {
            display: none;
        }
    }
`;

export const ViewModeTabButton = styled.div`
    border: 0;
    border-radius: 4px;
    position: absolute;
    right: 10px;
    top: 7px;
    padding: 0;
    background: none;
    text-align: center;
    color: #04265a;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
`;

export const ViewModeButton = styled.button<IButtonProps>`
    color: #04265a;
    background: #eee;
    border-radius: 0 4px 4px 0;
    border: 1px solid #ddd;
    border-left: 0;
    padding: 10px 15px;
    &.is-selected {
        background: ${theme.legacyColors.selectedBackground};
        border: 1px solid #${theme.legacyColors.selectedBorder};
        color: ${theme.legacyColors.selectedColor};
    }
    &:first-child {
        border-right: 1px solid #ddd;
        border-radius: 4px 0 0 4px;
    }
`;

export const FilterButton = styled.button<IButtonProps>`
    text-align: center;
    color: #04265a;
    cursor: pointer;
    font-size: 0.9rem;
    font-weight: 500;
    width: 40px;
    height: 30px;
    padding: 0;
    border-radius: 4px;
    background: #fff;
    border: 1px solid #ccc;
    box-shadow: 0 1px 10px -2px #ccc;
    &.clear-button {
        right: 60px;
        width: auto;
        padding: 0 6px;
        background: no-repeat;
        border: 0;
        &:hover {
            border: 1px solid #ccc;
            padding: 0 5px;
        }
    }

    &.issues-button {
        right: 60px;
        width: auto;
        padding: 0 6px;
        background: none;
        border: 0;
        color: red;
        &:hover {
            border: 1px solid #ccc;
            padding: 0 5px;
        }
    }

    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}
`;

export const FilterTextButton = styled(TextButton) <IButtonProps>`
    font-size: 0.75em;
    padding: 2px 6px;
    height: 22px;
    line-height: 0.6rem;
    display: flex;
    align-items: center;
    font-weight: 600;
    background: ${theme.colors.background.white};
    
    &.disabled {
        opacity: 0.5;
    }

    &.selected {
        background: ${theme.colors.background.selectedButton};
        color: ${theme.colors.text.white};
        border: 1px solid ${theme.colors.border.selected};
    }
`;


export const PinButton = styled.button<IButtonProps>`
    font-size: 0.9rem;
    color: #04265a;
    background: #fff;
    border: 1px solid #ccc;
    cursor: pointer;
    text-align: center;
    display: inline-block;
    height: 30px;
    width: 30px;
    margin: 0 10px 0 0;
    padding: 8px;
    line-height: 1;

    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}
`;

export const ToggleSummaryButton = styled.button<IButtonProps>`
    font-size: 0.7rem;
    color: ${({ theme }) => theme.colors.button.toggleSummary.color};
    background: ${({ theme }) => theme.colors.button.toggleSummary.background};
    border: 1px solid #ccc;
    cursor: pointer;
    text-align: center;
    display: inline-block;
    margin: 0 10px 0 0;
    padding: 2px 8px;

    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${theme.legacyColors.selectedBackground};
            border: 1px solid #${theme.legacyColors.selectedBorder};
            color: ${theme.legacyColors.selectedColor};
        `}
`;

export const IconBackButton = styled.button<IButtonProps>`
    background: transparent;
    border: 0;
    font-size: 1rem;
    cursor: pointer;
    :hover {
        border: none;
    }
`;

export const SectionHeaderButton = styled.button<IButtonProps>`
    border: 1px solid #b0bd94;
    background: #ffffff;
    color: #304405;
    vertical-align: middle;
    outline: 0;
    font-size: 0.7rem;
    font-weight: 500;
`;

export const ButtonDescriptionText = styled.div`
    font-size: 0.7rem;
    position: absolute;
    right: 63px;
    top: calc(50% - 7px);
`;


export const ButtonWrapper = styled.div<IPanelProps>`
    display: flex;
    justify-content: flex-end;
     ${({ alignLeft }) =>
        alignLeft &&
        css`
            justify-content: flex-start;
        `};
        ${({ alignCenter }) =>
        alignCenter &&
        css`
            justify-content: space-between;
        `};
    grid-gap: 2px;
    align-items: center;
    position: relative;
    min-width: 100px;
    @media print {
        display: none;
    }
`;

export const ProcessButtonWrapper = styled(ButtonWrapper)`
    padding: 0 0 0 10px;
    margin: 0 0 0 10px;
`;

export const PlainTextButton = styled.button<IButtonProps>`
    font-size: 1em;
    margin: 1px 5px 12px 5px;
    padding-left: 0.5em;
    padding-right: 0.5em;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    border: 1px solid #ccc;
    color: ${({ theme }) => theme.colors.text.plainButton};
    box-shadow: #ccc 0px 3px 10px -2px;

    @media only screen and (max-width: 1280px) {
        font-size: 0.8rem;
        padding: 0.4em 0.8em;
    }

    ${({ isSelected }) =>
        isSelected &&
        css`
            background: ${({ theme }) => theme.colors.background.selected};
        `}
`;
